// Terms
// TLRU       - Time aware Least Recently Used
// LRU record - least recently used cache record
// LLT record - least life time cache record
// LRU chain  - a chain of all cache records in access sequence. The head of the
//              chain is the LRU record
// TTL heap   - a min-heap of all cache records according to their time to live.
//              the root of the heap is the LLT record

import { ReplacementStrategy } from './base';
import { EXP_INVALIDATED, DEFAULT_LIMIT } from '../constants';
import {
  ILRURecord, ICacheLRUState, ITimeAwareCache, ICacheEntry, IReplacementOptions,
} from '../types';

function validateLimit(limit: number): void {
  if (typeof limit !== 'number' || Math.floor(limit) !== limit || limit <= 0) {
    throw new Error('Invalid limit');
  }
}

/* eslint-disable no-param-reassign */
function removeLRURecord(record: ILRURecord): void {
  record.prev.next = record.next;
  record.next.prev = record.prev;
}

function insertLRURecord(record: ILRURecord, anchor: ILRURecord): void {
  anchor.prev.next = record;
  record.prev = anchor.prev;
  anchor.prev = record;
  record.next = anchor;
}
/* eslint-enable no-param-reassign */

function promoteLRURecord(lru: ICacheLRUState, key: string): void {
  const { records, indices } = lru;
  const record = records[indices[key]];
  const pseudoRecord = records[0];

  if (record !== pseudoRecord && record !== pseudoRecord.prev) {
    removeLRURecord(record);
    insertLRURecord(record, pseudoRecord);
  }
}

/* eslint-disable no-bitwise, no-continue */
function adjustHeap(lru: ICacheLRUState, key: string): void {
  const { records, indices } = lru;
  let index = indices[key];

  while (index > 0 && index < records.length) {
    const record = records[index];

    const indexParent = index >> 1;
    const recordParent = indexParent > 0 ? records[indexParent] : null;

    if (recordParent && recordParent.exp! > record.exp!) {
      indices[record.key!] = indexParent;
      indices[recordParent.key!] = index;
      records[index] = recordParent;
      records[indexParent] = record;
      index = indexParent;
      continue;
    }

    const indexL = index << 1;
    const indexR = indexL + 1;
    const recordL = records[indexL] || null;
    const recordR = records[indexR] || null;
    const indexChild = (recordR && recordL && recordR.exp! < recordL.exp!) ? indexR : indexL;
    const recordChild = records[indexChild] || null;

    if (recordChild && recordChild.exp! < record.exp!) {
      indices[record.key!] = indexChild;
      indices[recordChild.key!] = index;
      records[index] = recordChild;
      records[indexChild] = record;
      index = indexChild;
      continue;
    }

    break;
  }
}
/* eslint-enable no-bitwise, no-continue */

export class LRUReplacementStrategy<TValue = unknown> extends ReplacementStrategy<TValue> {
  limit: number;

  /**
   * The Time aware Least Recently Used cache replacement strategy.
   * The replacement rules are
   *  1. If the LLT cache entry is expired, reuse it
   *  2. If the cache is not full, create a new entry
   *  3. Reuse the LRU cache entry
   * @extends ReplacementStrategy
   * @param {Object} options -
   * @param {number} options.limit - Maximum number of cache entries
   */
  constructor({ limit = DEFAULT_LIMIT }: IReplacementOptions = {}) {
    super();
    validateLimit(limit);
    this.limit = limit;
  }

  /**
   * Initialize the data structure to apply the strategy on a given cache
   * @override
   * @param {ITimeAwareCache} cache - The cache to work with
   * @returns {void}
   */
  initializeCache(cache: ITimeAwareCache<TValue>): void {
    super.initializeCache(cache);

    // The pseudoRecord, doesn't hold any data, the purpose is
    // 1. Hold the head and tail of the LRU chain. Make the linked list a ring
    //    to simplify the logic.
    // 2. Take the index 0 of the TTL heap, to simplify the calculation of the
    //    index of the parent and the children in the heap.
    const pseudoRecord: ILRURecord = {} as ILRURecord;
    pseudoRecord.next = pseudoRecord;
    pseudoRecord.prev = pseudoRecord;

    // eslint-disable-next-line no-param-reassign
    cache.lru = {
      records: [pseudoRecord],
      indices: {},
    };
  }

  /**
   * Get the key of the cache entry to replace, the rules are
   *  1. If the LLT entry is expired, reused it (return key of the LLT entry)
   *  2. If the cache is not full, allocate a new entry (return null)
   *  3. Reuse the LRU entry (return key of the LRU entry)
   * @override
   * @param {ITimeAwareCache} cache - The cache to write with
   * @param {string} key - The key for the new cache entry
   * @param {number} timestamp - The timestamp of the write operation
   * @returns {string} - The key of the entry to replace
   */
  keyToReplace(cache: ITimeAwareCache<TValue>, key: string, timestamp: number): string | null {
    const { records, indices } = cache.lru!;

    if (indices[key]) {
      return key;
    }

    const recordLLT = records[1] || null;

    if (recordLLT && recordLLT.exp! <= timestamp) {
      return recordLLT.key!;
    }

    if (records.length <= this.limit) {
      return null;
    }

    return records[0].next.key!;
  }

  /**
   * Update the LRU records onGet
   * @override
   * @param {ITimeAwareCache} cache - The cache being accessed
   * @param {number} timestamp - The timestamp of the operation
   * @param {ICacheEntry} entry - The cache entry being read, null on cache miss
   * @returns {void}
   */
  onGet(cache: ITimeAwareCache<TValue>, timestamp: number, entry: ICacheEntry<TValue>): void {
    if (entry.value !== null && entry.exp > timestamp) {
      promoteLRURecord(cache.lru!, entry.key);
    }
  }

  /**
   * Update the LRU records onSet
   * @override
   * @param {ITimeAwareCache} cache - The cache being accessed
   * @param {number} timestamp - The timestamp of the operation
   * @param {ICacheEntry} entry - The cache entry being written
   * @param {string} keyToReplace - The key of the replaced cache entry
   * @returns {void}
   */
  onSet(
    cache: ITimeAwareCache<TValue>,
    timestamp: number,
    entry: ICacheEntry<TValue>,
    keyToReplace: string | null
  ): void {
    const { records, indices } = cache.lru!;

    if (keyToReplace) {
      const index = indices[keyToReplace];
      const record = records[index];

      record.key = entry.key;
      record.exp = entry.exp;

      delete indices[keyToReplace];
      indices[entry.key] = index;

      promoteLRURecord(cache.lru!, entry.key);
    } else {
      const index = records.length;
      const record: ILRURecord = {
        key: entry.key, exp: entry.exp, next: {} as ILRURecord, prev: {} as ILRURecord,
      };
      indices[entry.key] = index;
      records.push(record);
      insertLRURecord(record, records[0]);
    }

    adjustHeap(cache.lru!, entry.key);
  }

  /**
   * Update the LRU records onDel
   * @override
   * @param {ITimeAwareCache} cache - The cache being accessed
   * @param {number} timestamp - The timestamp of the operation
   * @param {ICacheEntry} entry - The cache entry to delete, null on cache miss
   * @returns {void}
   */
  onDel(cache: ITimeAwareCache<TValue>, timestamp: number, entry: ICacheEntry<TValue>): void {
    if (entry.value !== null && entry.exp > EXP_INVALIDATED) {
      const { records, indices } = cache.lru!;
      const index = indices[entry.key];
      const record = records[index];

      record.exp = EXP_INVALIDATED;
      adjustHeap(cache.lru!, entry.key);
    }
  }
}
